import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { Search } from "lucide-react";
import type { BudgetLineVariance } from "@/gen/omnios/v1/accounting_pb";
import {
	useGetBudgetVariance,
	useSetBudgetAmount,
} from "@/hooks/use-accounting";
import { useAccountingContext } from "@/lib/accounting-context";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useGridLayout } from "@/lib/use-grid-layout";

import type {
	CellValueChangedEvent,
	ColDef,
	ValueFormatterParams,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/accounting/budget")({
	component: BudgetPage,
});

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

function BudgetPage() {
	const { t } = useTranslation("accounting");
	const navigate = useNavigate();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("budget");
	const { periodId } = useAccountingContext();
	const { data, isLoading } = useGetBudgetVariance(periodId);
	const setBudget = useSetBudgetAmount();

	useBreadcrumbs([{ label: t("nav_budget") }]);

	const lines = useMemo(() => data?.lines ?? [], [data]);

	const onCellValueChanged = useCallback(
		(event: CellValueChangedEvent<BudgetLineVariance>) => {
			if (event.colDef.field === "budgetAmount" && event.data) {
				const newValue = String(event.newValue ?? 0);
				setBudget.mutate(
					{
						periodId,
						accountCode: event.data.accountCode,
						amount: newValue,
					},
					{
						onSuccess: () =>
							toast.success(
								t("budget_saved", { defaultValue: "Бюджет сохранён" }),
							),
					},
				);
			}
		},
		[periodId, setBudget, t],
	);

	const columnDefs = useMemo<ColDef<BudgetLineVariance>[]>(
		() => [
			{
				field: "accountCode",
				headerName: t("account_code"),
				width: 90,
				pinned: "left" as const,
				cellClass: "font-mono cursor-pointer hover:underline text-primary",
			},
			{
				field: "accountName",
				headerName: t("account_name"),
				flex: 2,
				minWidth: 160,
			},
			{
				field: "budgetAmount",
				headerName: t("budget_amount"),
				flex: 1,
				minWidth: 110,
				type: "rightAligned",
				cellClass: "font-tabular",
				editable: true,
				valueSetter: (params) => {
					const val = Number.parseFloat(params.newValue);
					if (Number.isNaN(val)) return false;
					params.data.budgetAmount = String(val);
					return true;
				},
				valueGetter: (p) => Number.parseFloat(p.data?.budgetAmount || "0"),
				valueFormatter: (p: ValueFormatterParams) => formatAmount(p.value ?? 0),
				cellStyle: { backgroundColor: "rgba(59, 130, 246, 0.04)" },
			},
			{
				field: "actualAmount",
				headerName: t("actual_amount"),
				flex: 1,
				minWidth: 110,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.actualAmount || "0"),
				valueFormatter: (p: ValueFormatterParams) => formatAmount(p.value ?? 0),
			},
			{
				field: "variance",
				headerName: t("variance"),
				flex: 1,
				minWidth: 110,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.variance || "0"),
				valueFormatter: (p: ValueFormatterParams) => formatAmount(p.value ?? 0),
				cellStyle: (p) => ({
					color: (p.value ?? 0) >= 0 ? "hsl(var(--chart-2))" : "hsl(var(--destructive))",
				}),
			},
			{
				field: "variancePct",
				headerName: "%",
				width: 70,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueFormatter: (p: ValueFormatterParams<BudgetLineVariance>) =>
					p.value != null ? `${p.value.toFixed(1)}%` : "\u2014",
				cellStyle: (p) => ({
					color: (p.value ?? 0) >= 0 ? "hsl(var(--chart-2))" : "hsl(var(--destructive))",
				}),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const pinnedBottomRowData = useMemo(() => {
		if (!data) return [];
		return [
			{
				accountCode: t("total"),
				accountName: "",
				budgetAmount: data.totalBudget || "0",
				actualAmount: data.totalActual || "0",
				variance: data.totalVariance || "0",
				variancePct: 0,
			} as unknown as BudgetLineVariance,
		];
	}, [data, t]);

	const onCellClicked = useCallback(
		(e: { colDef: ColDef; data: BudgetLineVariance | undefined }) => {
			if (
				e.colDef.field === "accountCode" &&
				e.data &&
				e.data.accountCode !== t("total")
			) {
				navigate({
					to: "/accounting/accounts/$id",
					params: { id: e.data.accountCode },
				});
			}
		},
		[navigate, t],
	);

	const totalBudget = Number.parseFloat(data?.totalBudget || "0");
	const totalActual = Number.parseFloat(data?.totalActual || "0");
	const overUnder = totalActual - totalBudget;

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: `${lines.length} ${t("row_count_short", { defaultValue: "строк" })}`,
				align: "left" as const,
			},
			{
				id: "overunder",
				content:
					overUnder >= 0
						? `${t("budget_over", { defaultValue: "Перерасход" })}: ${formatAmount(overUnder)}`
						: `${t("budget_under", { defaultValue: "Экономия" })}: ${formatAmount(Math.abs(overUnder))}`,
				align: "left" as const,
			},
		],
		[lines.length, overUnder, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: `budget_${periodId}.csv`,
						})
					}
				>
					CSV
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
			<AgGridReact<BudgetLineVariance>
				ref={gridRef}
				rowData={lines}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				localeText={AG_GRID_LOCALE_RU}
				onGridReady={onGridReady}
				onSortChanged={onStateChanged}
				onFilterChanged={onStateChanged}
				onColumnMoved={onStateChanged}
				onColumnResized={onStateChanged}
				onColumnVisible={onStateChanged}
				singleClickEdit
				stopEditingWhenCellsLoseFocus
				onCellValueChanged={onCellValueChanged}
				onCellClicked={onCellClicked}
				rowHeight={28}
				headerHeight={32}
				animateRows={false}
				getRowId={(p) => p.data.accountCode}
				loading={isLoading}
				pinnedBottomRowData={pinnedBottomRowData}
				overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_budget_title", { defaultValue: "Бюджет не заполнен" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_budget_desc", { defaultValue: "Кликните в ячейку Бюджет, чтобы ввести сумму" })}</p></div>`}
			/>
			</div>
		</div>
	);
}
